import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import Header from '../../components/Header'
import { useTheme } from '../../contexts/ThemeContext'
import { useAuth } from '../../contexts/AuthContext'
import { leaderboardAPI, checkinsAPI, authAPI } from '../../config/api'
import ToastNotification from '../../components/ToastNotification'

export default function ProfilePage() {
  const { theme } = useTheme()
  const { user, isAuthenticated, loading: authLoading, refreshUser } = useAuth()
  const navigate = useNavigate()
  const [isEditingBio, setIsEditingBio] = useState(false)
  const [bio, setBio] = useState('')
  const [workoutDescription, setWorkoutDescription] = useState('')
  const [healthBefore, setHealthBefore] = useState(5)
  const [healthAfter, setHealthAfter] = useState(5)
  const [duration, setDuration] = useState('')
  const [painAreas, setPainAreas] = useState<string[]>([])
  const [reflection, setReflection] = useState('')
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [badges, setBadges] = useState<any[]>([])
  const [checkInCalendar, setCheckInCalendar] = useState<any>({})
  const [userStats, setUserStats] = useState<{ points: number; streak: number; maxStreak?: number; rank?: number }>({ points: 0, streak: 0 })
  const [weeklyStats, setWeeklyStats] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)

  const painAreaOptions = ['Háng', 'Đùi', 'Cổ Chân', 'Gân Kheo', 'Gối', 'Khác']

  useEffect(() => {
    // Chỉ redirect khi auth đã load xong và user chưa authenticated
    // Tránh redirect khi đang loading (có thể user đang được load từ localStorage)
    if (!authLoading && !isAuthenticated) {
      navigate('/login')
    }
  }, [isAuthenticated, authLoading, navigate])

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        
        // Fetch user data
        try {
          const userData = await authAPI.getMe()
          if (userData?.bio) {
            setBio(userData.bio)
          }
        } catch (bioError) {
          // Error fetching user bio
        }

        // Fetch profile data (includes badges and check_in_calendar)
        if (user?.id) {
          try {
            const profileData = await leaderboardAPI.getProfile(Number(user.id))
            
            // Fetch ranking stats
            try {
              const ranking = await leaderboardAPI.getMyRanking()
              setUserStats({
                points: ranking?.total_points || ranking?.points || profileData?.total_points || 0,
                streak: ranking?.streak || 0,
                maxStreak: ranking?.max_streak || 0,
                rank: ranking?.rank || 0
              })
            } catch (rankingError) {
              setUserStats({
                points: profileData?.total_points || profileData?.points || user?.points || 0,
                streak: profileData?.streak || user?.streak || 0,
                maxStreak: profileData?.max_streak || 0,
                rank: profileData?.rank || 0
              })
            }
            
            // Process badges
            if (profileData.badges) {
              let badgesArray = Array.isArray(profileData.badges) ? profileData.badges : (profileData.badges?.results || profileData.badges?.data || [])
              
              badgesArray = badgesArray.map((badge: any) => {
                if (badge.badge) {
                  return {
                    id: badge.badge.id,
                    name: badge.badge.name,
                    icon: badge.badge.icon || 'pi-star',
                    requirement: badge.badge.requirement,
                    points: badge.badge.points,
                    description: badge.badge.description,
                    color: badge.badge.color,
                    earned_date: badge.earned_date,
                    is_emoji: badge.badge.is_emoji || false,
                    earned: true
                  }
                }
                // Badge đã là flat object từ profile API
                return {
                  id: badge.id,
                  name: badge.name || badge.badge?.name,
                  icon: badge.icon || badge.badge?.icon || 'pi-star',
                  requirement: badge.requirement,
                  points: badge.points,
                  description: badge.description,
                  color: badge.color,
                  earned_date: badge.earned_date,
                  is_emoji: badge.is_emoji || false,
                  earned: badge.earned !== undefined ? badge.earned : true
                }
              })
              .filter((badge: any) => badge.id && badge.name)
              .filter((badge: any) => badge.earned === true)
              
              // Format badges for display - sử dụng description từ backend hoặc tạo từ requirement và points
              const formattedBadges = badgesArray.map((badge: any) => {
                // Nếu có description từ backend, dùng nó
                // Nếu không, tạo từ requirement và points
                let description = badge.description
                if (!description) {
                  if (badge.requirement && badge.points !== undefined) {
                    // Format: "3 buổi • +1 điểm" hoặc "TUẦN 1 • +5 điểm"
                    if (typeof badge.requirement === 'string' && badge.requirement.includes('TUẦN')) {
                      description = `${badge.requirement} • +${badge.points} điểm`
                    } else {
                      description = `${badge.requirement} buổi • +${badge.points} điểm`
                    }
                  } else {
                    description = `${badge.requirement || 'N/A'} • +${badge.points || 0} điểm`
                  }
                }
                
                return {
                  id: badge.id,
                  icon: badge.icon || 'pi-star',
                  title: badge.name,
                  description: description,
                  color: badge.color || 'text-yellow-400',
                  earned: true,
                  is_emoji: badge.is_emoji || false
                }
              })
              
              setBadges(formattedBadges)
            } else {
              setBadges([])
            }
            
            // Process check_in_calendar
            if (profileData.check_in_calendar) {
              setCheckInCalendar(profileData.check_in_calendar)
            } else {
              // Fallback to getCalendar API
              try {
                const now = new Date()
                const calendarData = await checkinsAPI.getCalendar({
                  year: now.getFullYear(),
                  month: now.getMonth() + 1
                })
                setCheckInCalendar(calendarData || {})
              } catch (calendarError) {
                setCheckInCalendar({})
              }
            }
          } catch (profileError) {
            // Fallback to individual APIs
            try {
              const badgesData = await leaderboardAPI.getMyBadges()
              const badgesArray = Array.isArray(badgesData) ? badgesData : (badgesData?.results || badgesData?.data || [])
              const formattedBadges = badgesArray.map((badge: any) => ({
                id: badge.id,
                icon: badge.icon || 'pi-star',
                title: badge.name,
                description: badge.description || `${badge.requirement || 'N/A'} • +${badge.points || 0} điểm`,
                color: badge.color || 'text-yellow-400',
                earned: badge.earned !== undefined ? badge.earned : true,
                is_emoji: badge.is_emoji || false
              }))
              const earnedBadges = formattedBadges.filter((badge: any) => badge.earned === true)
              setBadges(earnedBadges)
            } catch (badgeError) {
              setBadges([])
            }
            
            try {
              const now = new Date()
              const calendarData = await checkinsAPI.getCalendar({
                year: now.getFullYear(),
                month: now.getMonth() + 1
              })
              setCheckInCalendar(calendarData || {})
            } catch (calendarError) {
              setCheckInCalendar({})
            }
          }
        }

        // Fetch weekly stats
        try {
          const stats = await checkinsAPI.getWeeklyStats()
          setWeeklyStats(Array.isArray(stats) ? stats : [])
        } catch (statsError) {
          setWeeklyStats([])
        }
      } catch (error) {
        // Error fetching profile data
      } finally {
        setLoading(false)
      }
    }

    // Chỉ fetch data khi auth đã load xong và user đã authenticated
    if (!authLoading && isAuthenticated && user?.id) {
      fetchData()
    } else if (!authLoading && !isAuthenticated) {
      // Nếu đã load xong nhưng không authenticated, set loading = false để hiển thị redirect
      setLoading(false)
    }
  }, [isAuthenticated, authLoading, user])

  const handleBioSave = async () => {
    try {
      setSaving(true)
      await authAPI.updateProfile({ bio })
      setIsEditingBio(false)
      setToast({ message: 'Đã cập nhật giới thiệu!', type: 'success' })
      if (refreshUser) {
        await refreshUser()
      }
    } catch (error: any) {
      setToast({ message: error.response?.data?.message || 'Có lỗi xảy ra khi cập nhật', type: 'error' })
    } finally {
      setSaving(false)
    }
  }

  const handlePainAreaToggle = (area: string) => {
    setPainAreas(prev =>
      prev.includes(area) ? prev.filter(a => a !== area) : [...prev, area]
    )
  }

  const handleCalendarDateClick = async (date: number) => {
    // Lấy currentMonth và currentYear từ checkInCalendar để đảm bảo đúng tháng/năm đang xem
    const currentMonth = checkInCalendar?.current_month || new Date().getMonth() + 1
    const currentYear = checkInCalendar?.current_year || new Date().getFullYear()
    // Tạo Date object theo local timezone để tránh lệch ngày
    const selectedDateObj = new Date(currentYear, currentMonth - 1, date)
    // Format date string theo YYYY-MM-DD
    const year = selectedDateObj.getFullYear()
    const month = String(selectedDateObj.getMonth() + 1).padStart(2, '0')
    const day = String(selectedDateObj.getDate()).padStart(2, '0')
    const dateStr = `${year}-${month}-${day}`
    
    // Kiểm tra xem ngày này có điểm danh không
    let dates: number[] = []
    if (checkInCalendar?.check_ins && Array.isArray(checkInCalendar.check_ins)) {
      if (checkInCalendar.check_ins.length > 0 && typeof checkInCalendar.check_ins[0] === 'number') {
        // Array of day numbers from profile API
        dates = checkInCalendar.check_ins.filter((day: number) => day >= 1 && day <= daysInMonth)
      } else {
        // Array of objects from getCalendar API
        dates = checkInCalendar.check_ins.map((ci: any) => {
          const dateStr = ci.check_in_date || ci.date || ci
          if (!dateStr) return null
          // Parse date string an toàn để tránh lệch timezone
          let dateObj: Date
          if (typeof dateStr === 'string' && dateStr.match(/^\d{4}-\d{2}-\d{2}(T|$)/)) {
            // ISO date string (YYYY-MM-DD hoặc YYYY-MM-DDTHH:mm:ss)
            const datePart = dateStr.split('T')[0]
            const [year, month, day] = datePart.split('-').map(Number)
            dateObj = new Date(year, month - 1, day) // month - 1 vì Date month bắt đầu từ 0
          } else {
            dateObj = new Date(dateStr)
          }
          if (isNaN(dateObj.getTime())) return null
          if (dateObj.getMonth() + 1 === currentMonth && dateObj.getFullYear() === currentYear) {
            return dateObj.getDate()
          }
          return null
        }).filter((d: number | null) => d !== null) as number[]
      }
    } else if (checkInCalendar?.check_in_details) {
      dates = Object.keys(checkInCalendar.check_in_details)
        .map((day: string) => {
          const dayNum = parseInt(day, 10)
          return isNaN(dayNum) ? null : dayNum
        })
        .filter((day: number | null) => day !== null && day >= 1 && day <= daysInMonth) as number[]
    }
    
    const isCheckedIn = dates.includes(date)
    if (!isCheckedIn) {
      return
    }

    setSelectedDate(selectedDateObj)
    
    try {
      // Load nhật ký của ngày đó
      const diary = await checkinsAPI.getDiaryByDate(dateStr)
      
      // Điền dữ liệu vào form
      setWorkoutDescription(diary.description || '')
      setHealthBefore(diary.health_before || 5)
      setHealthAfter(diary.health_after || 5)
      setDuration(diary.duration ? String(diary.duration) : '')
      setPainAreas(diary.pain_areas || [])
      setReflection(diary.reflection || '')
    } catch (error: any) {
      // Nếu không có nhật ký, reset form về trạng thái mặc định
      if (error.response?.status === 404) {
        setWorkoutDescription('')
        setHealthBefore(5)
        setHealthAfter(5)
        setDuration('')
        setPainAreas([])
        setReflection('')
      } else {
        // Error loading diary
      }
    }
  }

  const handleSaveDiary = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      setSaving(true)
      
      // Đảm bảo gửi đúng format dữ liệu
      const diaryData: any = {}
      
      if (workoutDescription) {
        diaryData.description = workoutDescription
      }
      if (duration) {
        diaryData.duration = parseInt(duration)
      }
      if (healthBefore) {
        diaryData.health_before = healthBefore
      }
      if (healthAfter) {
        diaryData.health_after = healthAfter
      }
      if (painAreas && painAreas.length > 0) {
        diaryData.pain_areas = painAreas
      }
      if (reflection) {
        diaryData.reflection = reflection
      }
      
      // Nếu có selectedDate (click vào ô xanh), gửi kèm date để update đúng ngày đó
      if (selectedDate) {
        const dateStr = selectedDate.toISOString().split('T')[0]
        diaryData.date = dateStr
      }
      
      await checkinsAPI.createWorkoutDiary(diaryData)
      const dateMessage = selectedDate 
        ? ` ngày ${selectedDate.getDate()}/${selectedDate.getMonth() + 1}/${selectedDate.getFullYear()}`
        : ''
      setToast({ message: `Đã lưu nhật ký tập luyện${dateMessage}!`, type: 'success' })
      
      // Reset form
      setWorkoutDescription('')
      setDuration('')
      setHealthBefore(5)
      setHealthAfter(5)
      setPainAreas([])
      setReflection('')
      setSelectedDate(null)
      
      // Refresh weekly stats
      try {
        const stats = await checkinsAPI.getWeeklyStats()
        setWeeklyStats(Array.isArray(stats) ? stats : [])
      } catch (error) {
        // Error refreshing weekly stats
      }
    } catch (error: any) {
      // Error saving workout diary
      
      // Lấy thông báo lỗi chi tiết
      let errorMessage = 'Có lỗi xảy ra khi lưu nhật ký'
      
      if (error.response?.data) {
        const errorData = error.response.data
        
        // Ưu tiên hiển thị message hoặc error từ backend
        if (errorData.message) {
          errorMessage = errorData.message
        } else if (errorData.error) {
          errorMessage = errorData.error
        } else if (errorData.detail) {
          errorMessage = errorData.detail
        } else if (errorData.non_field_errors && errorData.non_field_errors.length > 0) {
          errorMessage = errorData.non_field_errors[0]
        } else {
          // Hiển thị lỗi từ field đầu tiên
          const firstError = Object.values(errorData)[0]
          if (Array.isArray(firstError) && firstError.length > 0) {
            errorMessage = String(firstError[0])
          } else if (typeof firstError === 'string') {
            errorMessage = firstError
          }
        }
      }
      
      setToast({ message: errorMessage, type: 'error' })
    } finally {
      setSaving(false)
    }
  }

  const now = new Date()
  const currentMonth = now.getMonth() + 1
  const currentYear = now.getFullYear()
  const daysInMonth = new Date(currentYear, currentMonth, 0).getDate()
  const firstDayOfMonth = new Date(currentYear, currentMonth - 1, 1).getDay()

  // Process check-in dates from calendar data
  // Format from profile API: { check_ins: [1, 2, 3, ...], check_in_details: {...}, current_month: ..., current_year: ... }
  // Format from getCalendar API: { check_ins: [{check_in_date: '...'}, ...], year: ..., month: ... }
  const checkInDates = (() => {
    if (!checkInCalendar || Object.keys(checkInCalendar).length === 0) {
      return []
    }
    
    // If check_ins is array of numbers (from profile API)
    if (checkInCalendar.check_ins && Array.isArray(checkInCalendar.check_ins)) {
      // Check if first element is a number (day number) or object (from getCalendar API)
      if (checkInCalendar.check_ins.length > 0 && typeof checkInCalendar.check_ins[0] === 'number') {
        // Array of day numbers from profile API
        return checkInCalendar.check_ins.filter((day: number) => day >= 1 && day <= daysInMonth)
      } else {
        // Array of objects from getCalendar API
        return checkInCalendar.check_ins.map((ci: any) => {
          const dateStr = ci.check_in_date || ci.date || ci
          if (!dateStr) return null
          // Parse date string an toàn để tránh lệch timezone
          let date: Date
          if (typeof dateStr === 'string' && dateStr.match(/^\d{4}-\d{2}-\d{2}(T|$)/)) {
            // ISO date string (YYYY-MM-DD hoặc YYYY-MM-DDTHH:mm:ss)
            const datePart = dateStr.split('T')[0]
            const [year, month, day] = datePart.split('-').map(Number)
            date = new Date(year, month - 1, day) // month - 1 vì Date month bắt đầu từ 0
          } else {
            date = new Date(dateStr)
          }
          if (isNaN(date.getTime())) return null
          if (date.getMonth() + 1 === currentMonth && date.getFullYear() === currentYear) {
            return date.getDate()
          }
          return null
        }).filter((date: number | null) => date !== null) || []
      }
    }
    
    // If we have check_in_details, extract day numbers from keys
    if (checkInCalendar.check_in_details) {
      return Object.keys(checkInCalendar.check_in_details)
        .map((day: string) => {
          const dayNum = parseInt(day, 10)
          return isNaN(dayNum) ? null : dayNum
        })
        .filter((day: number | null) => day !== null && day >= 1 && day <= daysInMonth) || []
    }
    
    return []
  })()

  // Nếu không authenticated sau khi đã load xong, redirect sẽ được xử lý bởi useEffect
  if (!authLoading && !isAuthenticated) {
    return null
  }

  return (
    <main className={`min-h-screen ${theme === 'dark' ? 'bg-black' : 'bg-gray-200'}`}>
      <Header />
      {toast && (
        <ToastNotification
          message={toast.message}
          type={toast.type}
          isVisible={!!toast}
          onClose={() => setToast(null)}
        />
      )}

      <section className="py-8 md:py-12 px-4">
        <div className="container mx-auto max-w-4xl">
          <div className={`rounded-2xl p-6 md:p-8 mb-6 ${theme === 'dark' ? 'bg-black/80 border border-gold-500/30' : 'bg-white border border-gold-400/30'}`}>
            <div className="flex flex-col items-center mb-6">
              <div className="relative group">
                <div className={`w-24 h-24 md:w-32 md:h-32 rounded-full flex items-center justify-center mb-4 ${theme === 'dark' ? 'bg-gold-500/30' : 'bg-gold-400/30'} overflow-hidden`}>
                  {user?.avatar ? (
                    <img
                      src={user.avatar}
                      alt={user.name}
                      className="w-full h-full rounded-full object-cover"
                    />
                  ) : (
                    <span className={`text-4xl md:text-5xl font-bold ${theme === 'dark' ? 'text-gold-400' : 'text-gold-500'}`}>
                      {user?.name?.charAt(0).toUpperCase() || 'U'}
                    </span>
                  )}
                </div>
                <label className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity">
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0]
                      if (file) {
                        try {
                          setSaving(true)
                          await authAPI.updateProfile({ avatar: file })
                          setToast({ message: 'Đã cập nhật avatar!', type: 'success' })
                          if (refreshUser) {
                            await refreshUser()
                          }
                        } catch (error: any) {
                          setToast({ message: error.response?.data?.message || 'Có lỗi xảy ra khi cập nhật avatar', type: 'error' })
                        } finally {
                          setSaving(false)
                        }
                      }
                    }}
                    disabled={saving}
                  />
                  <i className={`pi pi-camera text-white text-xl md:text-2xl ${saving ? 'opacity-50' : ''}`}></i>
                </label>
              </div>

              <h1 className={`text-2xl md:text-3xl font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                {user?.name || 'User'}
              </h1>

              <div className="flex items-center gap-4 md:gap-6 mb-4 flex-wrap justify-center">
                <div className="flex items-center gap-2">
                  <i className={`pi pi-trophy text-xl ${theme === 'dark' ? 'text-gold-400' : 'text-gold-500'}`}></i>
                  <span className={`text-sm md:text-base ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    {userStats.points} điểm
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <i className={`pi pi-star text-xl ${theme === 'dark' ? 'text-yellow-400' : 'text-yellow-500'}`}></i>
                  <span className={`text-sm md:text-base ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    {userStats.maxStreak || 0} chuỗi max
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <i className={`pi pi-sort-numeric-up text-xl ${theme === 'dark' ? 'text-blue-400' : 'text-blue-500'}`}></i>
                  <span className={`text-sm md:text-base ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    Hạng {userStats.rank || 0}
                  </span>
                </div>
              </div>

              <div className="w-full max-w-2xl">
                {isEditingBio ? (
                  <div className="space-y-2">
                    <textarea
                      value={bio}
                      onChange={(e) => setBio(e.target.value)}
                      placeholder="Mô tả về bản thân..."
                      className={`w-full px-4 py-2 rounded-lg border-2 ${theme === 'dark' ? 'bg-black border-gold-500 text-white' : 'bg-white border-gold-400 text-gray-900'} focus:outline-none focus:border-gold-400`}
                      rows={3}
                    />
                    <div className="flex gap-2 justify-end">
                      <button
                        onClick={() => setIsEditingBio(false)}
                        className={`px-4 py-2 rounded-lg transition ${theme === 'dark' ? 'text-gray-300 hover:bg-gray-800' : 'text-gray-700 hover:bg-gray-200'}`}
                      >
                        Hủy
                      </button>
                      <button
                        onClick={handleBioSave}
                        disabled={saving}
                        className="px-4 py-2 bg-gold-gradient text-black font-bold rounded-lg hover:shadow-lg transition disabled:opacity-50"
                      >
                        {saving ? 'Đang lưu...' : 'Lưu'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div
                    onClick={() => setIsEditingBio(true)}
                    className={`w-full px-4 py-3 rounded-lg border-2 border-dashed cursor-pointer transition ${theme === 'dark' ? 'border-gold-500/30 hover:border-gold-500/50 text-gray-400 hover:text-white' : 'border-gold-400/30 hover:border-gold-400/50 text-gray-600 hover:text-gray-900'}`}
                  >
                    {bio || 'Chưa có giới thiệu. Bấm để thêm...'}
                  </div>
                )}
              </div>

              <div className="w-full max-w-2xl mt-6">
                <Link
                  to="/checkin"
                  className={`w-full py-4 px-6 bg-gold-gradient text-black font-bold text-sm md:text-lg rounded-lg hover:shadow-lg hover:shadow-gold-500/50 transition transform hover:scale-105 flex items-center justify-center gap-3`}
                >
                  <i className="pi pi-calendar text-lg md:text-xl"></i>
                  <span>ĐIỂM DANH HÔM NAY</span>
                </Link>
              </div>
            </div>
          </div>

          {/* Badges Section */}
          <div className={`rounded-2xl p-6 md:p-8 mb-6 ${theme === 'dark' ? 'bg-black/80 border border-gold-500/30' : 'bg-white border border-gold-400/30'}`}>
            <div className="flex items-center gap-3 mb-6">
              <i className={`pi pi-trophy text-lg md:text-2xl ${theme === 'dark' ? 'text-gold-400' : 'text-gold-500'}`}></i>
              <h2 className={`text-lg md:text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                Huy Hiệu Đạt Được
              </h2>
            </div>

            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gold-400 mx-auto mb-4"></div>
                <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Đang tải huy hiệu...</p>
              </div>
            ) : badges.length === 0 ? (
              <p className={`text-center py-8 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                Chưa có huy hiệu nào
              </p>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {badges.map((badge) => (
                <div
                  key={badge.id}
                  className={`rounded-xl p-4 border-2 transition ${theme === 'dark'
                    ? 'bg-black/50 border-gold-500/30 hover:border-gold-500/50'
                    : 'bg-gray-100 border-gold-400/30 hover:border-gold-400/50'
                    }`}
                >
                  <div className="text-center">
                    {badge.is_emoji ? (
                      <span className={`text-2xl md:text-4xl mb-2 block ${badge.color}`}>{badge.icon}</span>
                    ) : (
                      <i className={`pi ${badge.icon} text-2xl md:text-4xl mb-2 ${badge.color}`}></i>
                    )}
                    <h3 className={`text-xs md:text-sm font-bold mb-1 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      {badge.title}
                    </h3>
                    <p className={`text-[10px] md:text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                      {badge.description}
                    </p>
                  </div>
                </div>
              ))}
              </div>
            )}
          </div>

          {/* Workout Diary Section */}
          <div className={`rounded-2xl p-6 md:p-8 mb-6 ${theme === 'dark' ? 'bg-black/80 border border-gold-500/30' : 'bg-white border border-gold-400/30'}`}>
            <form onSubmit={handleSaveDiary} className="space-y-6">
              {/* Title */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <i className={`pi pi-book text-lg md:text-xl ${theme === 'dark' ? 'text-gold-400' : 'text-gold-500'}`}></i>
                  <h2 className={`text-lg md:text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    {selectedDate 
                      ? `Nhật Ký Tập Luyện - ${selectedDate.getDate()}/${selectedDate.getMonth() + 1}/${selectedDate.getFullYear()}`
                      : 'Nhật Ký Tập Luyện Hôm Nay'
                    }
                  </h2>
                </div>
                {selectedDate && (
                  <button
                    type="button"
                    onClick={async () => {
                      // Load nhật ký hôm nay
                      const today = new Date()
                      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`
                      
                      setSelectedDate(null)
                      
                      try {
                        // Load nhật ký hôm nay
                        const diary = await checkinsAPI.getDiaryByDate(todayStr)
                        setWorkoutDescription(diary.description || '')
                        setHealthBefore(diary.health_before || 5)
                        setHealthAfter(diary.health_after || 5)
                        setDuration(diary.duration ? String(diary.duration) : '')
                        setPainAreas(diary.pain_areas || [])
                        setReflection(diary.reflection || '')
                      } catch (error: any) {
                        // Nếu không có nhật ký, reset form về trạng thái mặc định
                        if (error.response?.status === 404) {
                          setWorkoutDescription('')
                          setHealthBefore(5)
                          setHealthAfter(5)
                          setDuration('')
                          setPainAreas([])
                          setReflection('')
                        } else {
                          // Error loading today diary
                        }
                      }
                    }}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${
                      theme === 'dark'
                        ? 'bg-gold-500/20 hover:bg-gold-500/30 text-gold-400 border border-gold-500/50'
                        : 'bg-gold-100 hover:bg-gold-200 text-gold-600 border border-gold-400/50'
                    }`}
                  >
                    <i className="pi pi-calendar mr-2"></i>
                    Hôm nay
                  </button>
                )}
              </div>

              {/* Workout Description */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <i className={`pi pi-pencil text-xs md:text-sm ${theme === 'dark' ? 'text-gold-400' : 'text-gold-500'}`}></i>
                  <label className={`text-sm md:text-base font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    Hôm nay tập gì?
                  </label>
                </div>
                <textarea
                  value={workoutDescription}
                  onChange={(e) => setWorkoutDescription(e.target.value)}
                  placeholder="Mô tả buổi tập của bạn..."
                  maxLength={200}
                  className={`w-full px-4 py-3 rounded-lg border-2 ${theme === 'dark' ? 'bg-black border-gold-500/30 text-white' : 'bg-white border-gold-400/30 text-gray-900'} focus:outline-none focus:border-gold-400 resize-none`}
                  rows={4}
                />
                <p className={`text-xs mt-1 text-right ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                  {workoutDescription.length}/200 ký tự
                </p>
              </div>

              {/* Health Sliders */}
              <div className="grid md:grid-cols-2 gap-6">
                {/* Health Before */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <i className={`pi pi-heart text-sm md:text-lg ${theme === 'dark' ? 'text-gold-400' : 'text-gold-500'}`}></i>
                    <label className={`text-sm md:text-base font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      Sức khỏe trước tập
                    </label>
                  </div>
                  <div className="relative">
                    <input
                      type="range"
                      min="1"
                      max="10"
                      value={healthBefore}
                      onChange={(e) => setHealthBefore(Number(e.target.value))}
                      className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                      style={{
                        background: `linear-gradient(to right, ${theme === 'dark' ? '#DFAC2A' : '#DFAC2A'} 0%, ${theme === 'dark' ? '#DFAC2A' : '#DFAC2A'} ${(healthBefore - 1) * 11.11}%, ${theme === 'dark' ? '#374151' : '#d1d5db'} ${(healthBefore - 1) * 11.11}%, ${theme === 'dark' ? '#374151' : '#d1d5db'} 100%)`
                      }}
                    />
                    <div className="flex justify-between mt-1">
                      <span className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>1</span>
                      <span className={`text-lg font-bold ${theme === 'dark' ? 'text-gold-400' : 'text-gold-500'}`}>
                        {healthBefore}
                      </span>
                      <span className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>10</span>
                    </div>
                  </div>
                </div>

                {/* Health After */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <i className={`pi pi-heart text-sm md:text-lg text-green-400`}></i>
                    <label className={`text-sm md:text-base font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      Sức khỏe sau tập
                    </label>
                  </div>
                  <div className="relative">
                    <input
                      type="range"
                      min="1"
                      max="10"
                      value={healthAfter}
                      onChange={(e) => setHealthAfter(Number(e.target.value))}
                      className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                      style={{
                        background: `linear-gradient(to right, ${theme === 'dark' ? '#DFAC2A' : '#DFAC2A'} 0%, ${theme === 'dark' ? '#DFAC2A' : '#DFAC2A'} ${(healthAfter - 1) * 11.11}%, ${theme === 'dark' ? '#374151' : '#d1d5db'} ${(healthAfter - 1) * 11.11}%, ${theme === 'dark' ? '#374151' : '#d1d5db'} 100%)`
                      }}
                    />
                    <div className="flex justify-between mt-1">
                      <span className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>1</span>
                      <span className={`text-lg font-bold ${theme === 'dark' ? 'text-gold-400' : 'text-gold-500'}`}>
                        {healthAfter}
                      </span>
                      <span className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>10</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Duration Input */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <i className={`pi pi-clock text-xs md:text-sm ${theme === 'dark' ? 'text-gold-400' : 'text-gold-500'}`}></i>
                  <label className={`text-sm md:text-base font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    Thời gian tập (phút)
                  </label>
                </div>
                <input
                  type="number"
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  placeholder="Số phút"
                  min="0"
                  className={`w-full px-4 py-3 rounded-lg border-2 ${theme === 'dark' ? 'bg-black border-gold-500/30 text-white' : 'bg-white border-gold-400/30 text-gray-900'} focus:outline-none focus:border-gold-400`}
                />
              </div>

              {/* 7 Days Chart */}
              {weeklyStats.length > 0 && (
                <div className="mt-6">
                  <div className="flex items-center gap-2 mb-4">
                    <i className={`pi pi-chart-bar text-xs md:text-sm ${theme === 'dark' ? 'text-gold-400' : 'text-gold-500'}`}></i>
                    <label className={`text-sm md:text-base font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      7 ngày luyện tập gần nhất
                    </label>
                  </div>
                  <div className={`p-4 md:p-5 overflow-hidden ${theme === 'dark' ? 'bg-black/50' : 'bg-gray-100/50'} w-full`}>
                    <div className="flex items-end justify-between gap-0.5 md:gap-1 h-32 md:h-36 px-1">
                      {weeklyStats.map((stat, index) => {
                        const maxDuration = Math.max(...weeklyStats.map((s: any) => s.duration || 0), 1)
                        const height = maxDuration > 0 ? (stat.duration / maxDuration) * 100 : 0
                        const isToday = index === weeklyStats.length - 1
                        // Parse date string an toàn để tránh lệch timezone
                        // Backend trả về ISO date string (ví dụ: "2024-01-15")
                        let date: Date
                        if (stat.date) {
                          // Nếu là ISO date string (YYYY-MM-DD), parse theo local timezone
                          if (typeof stat.date === 'string' && stat.date.match(/^\d{4}-\d{2}-\d{2}$/)) {
                            const [year, month, day] = stat.date.split('-').map(Number)
                            date = new Date(year, month - 1, day) // month - 1 vì Date month bắt đầu từ 0
                          } else {
                            date = new Date(stat.date)
                          }
                        } else {
                          date = new Date()
                        }
                        const dayNumber = date.getDate()
                        
                        return (
                          <div key={index} className="flex-1 flex flex-col items-center gap-1 min-w-0 max-w-full">
                            <div className="relative w-full flex items-end justify-center mb-1" style={{ height: '100px' }}>
                              <div
                                className={`w-full max-w-full transition-all ${
                                  isToday
                                    ? theme === 'dark'
                                      ? 'bg-gold-gradient'
                                      : 'bg-gold-400'
                                    : theme === 'dark'
                                    ? 'bg-gold-500/50'
                                    : 'bg-gold-400/60'
                                }`}
                                style={{ height: `${height}%`, minHeight: stat.duration > 0 ? '4px' : '0' }}
                                title={`${stat.day_name}: ${stat.duration} phút`}
                              />
                            </div>
                            <div className="text-center w-full min-w-0 px-0.5">
                              <div className={`text-[9px] md:text-[10px] font-bold leading-tight ${isToday ? theme === 'dark' ? 'text-gold-400' : 'text-gold-500' : theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                                {stat.duration || 0}
                              </div>
                              <div className={`text-[9px] md:text-[10px] mt-1 leading-tight ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
                                {isToday ? 'Nay' : dayNumber.toString().padStart(2, '0')}
                              </div>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* Pain Areas */}
              <div>
                <label className={`block text-sm md:text-base font-semibold mb-3 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  Tình trạng cơ thể (chọn vùng đau)
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {painAreaOptions.map((area) => (
                    <button
                      key={area}
                      type="button"
                      onClick={() => handlePainAreaToggle(area)}
                      className={`px-3 py-2 md:px-4 md:py-3 rounded-lg border-2 transition text-xs md:text-sm ${
                        theme === 'dark'
                          ? painAreas.includes(area)
                            ? 'bg-gold-500/20 border-gold-500 text-gold-400'
                            : 'bg-black/50 border-gold-500/30 text-gray-300 hover:border-gold-500/50'
                          : painAreas.includes(area)
                          ? 'bg-gold-400/20 border-gold-500 text-gold-600'
                          : 'bg-gray-100 border-gold-400/30 text-gray-700 hover:border-gold-400/50'
                      }`}
                    >
                      {area}
                    </button>
                  ))}
                </div>
              </div>

              {/* Reflection Input */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <i className={`pi pi-heart text-xs md:text-sm ${theme === 'dark' ? 'text-gold-400' : 'text-gold-500'}`}></i>
                  <label className={`text-sm md:text-base font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    Cảm nhận bản thân sau buổi tập
                  </label>
                </div>
                <textarea
                  value={reflection}
                  onChange={(e) => setReflection(e.target.value)}
                  placeholder="Chia sẻ cảm nhận của bạn sau buổi tập..."
                  maxLength={500}
                  className={`w-full px-4 py-3 rounded-lg border-2 ${theme === 'dark' ? 'bg-black border-gold-500/30 text-white' : 'bg-white border-gold-400/30 text-gray-900'} focus:outline-none focus:border-gold-400 resize-none`}
                  rows={4}
                />
                <p className={`text-xs mt-1 text-right ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                  {reflection.length}/500 ký tự
                </p>
              </div>

              {/* Selected Date Info */}
              {selectedDate && (
                <div className={`p-3 rounded-lg flex items-center justify-between ${theme === 'dark' ? 'bg-gold-500/20 border border-gold-500/50' : 'bg-gold-100/50 border border-gold-400/50'}`}>
                  <p className={`text-sm ${theme === 'dark' ? 'text-gold-400' : 'text-gold-600'}`}>
                    <i className="pi pi-info-circle mr-2"></i>
                    Đang xem/chỉnh sửa nhật ký ngày {selectedDate.getDate()}/{selectedDate.getMonth() + 1}/{selectedDate.getFullYear()}
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      // Clear selectedDate và reset tất cả các trường form
                      setSelectedDate(null)
                      setWorkoutDescription('')
                      setDuration('')
                      setHealthBefore(5)
                      setHealthAfter(5)
                      setPainAreas([])
                      setReflection('')
                    }}
                    className={`text-xs px-2 py-1 rounded ${theme === 'dark' ? 'hover:bg-gold-500/30 text-gold-400' : 'hover:bg-gold-200 text-gold-600'}`}
                    title="Đóng và xóa dữ liệu"
                  >
                    <i className="pi pi-times"></i>
                  </button>
                </div>
              )}

              {/* Save Button */}
              <button
                type="submit"
                disabled={saving}
                className="w-full py-4 px-6 bg-gold-gradient text-black font-bold text-sm md:text-lg rounded-lg hover:shadow-lg hover:shadow-gold-500/50 transition transform hover:scale-105 flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              >
                <i className="pi pi-save text-lg md:text-xl"></i>
                <span>
                  {saving 
                    ? 'ĐANG LƯU...' 
                    : selectedDate 
                      ? `LƯU NHẬT KÝ - ${selectedDate.getDate()}/${selectedDate.getMonth() + 1}/${selectedDate.getFullYear()}`
                      : 'LƯU NHẬT KÝ HÔM NAY'
                  }
                </span>
              </button>
            </form>
          </div>

          {/* Attendance Calendar Section */}
          <div className={`rounded-2xl p-6 md:p-8 mb-6 ${theme === 'dark' ? 'bg-black/80 border border-gold-500/30' : 'bg-white border border-gold-400/30'}`}>
            <div className="flex items-center gap-2 mb-6">
              <i className={`pi pi-calendar text-lg md:text-xl ${theme === 'dark' ? 'text-gold-400' : 'text-gold-500'}`}></i>
              <h2 className={`text-base md:text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                Lịch Điểm Danh - tháng {currentMonth}/{currentYear}
              </h2>
            </div>

            {/* Calendar Grid */}
            <div className="mb-4">
              {/* Days of Week Header */}
              <div className="grid grid-cols-7 gap-2 mb-2">
                {['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'].map((day) => (
                  <div
                    key={day}
                    className={`text-center text-sm font-semibold py-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}
                  >
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar Dates */}
              <div className="grid grid-cols-7 gap-2">
                {/* Empty cells for days before first day of month */}
                {Array.from({ length: firstDayOfMonth }).map((_, i) => (
                  <div key={`empty-${i}`} className="aspect-square"></div>
                ))}

                {/* Date cells */}
                {Array.from({ length: daysInMonth }, (_, i) => {
                  const date = i + 1
                  const today = new Date()
                  const isToday = today.getDate() === date && today.getMonth() === currentMonth - 1 && today.getFullYear() === currentYear
                  const isCheckedIn = checkInDates.includes(date)

                  return (
                    <div
                      key={date}
                      onClick={() => isCheckedIn && handleCalendarDateClick(date)}
                      className={`aspect-square flex items-center justify-center rounded-lg text-sm font-semibold transition ${
                        isCheckedIn
                          ? 'bg-green-500 text-white cursor-pointer hover:bg-green-600'
                          : isToday
                          ? `${theme === 'dark' ? 'bg-gold-500/30 border-2 border-gold-500' : 'bg-gold-400/30 border-2 border-gold-400'} ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`
                          : theme === 'dark'
                          ? 'text-white hover:bg-gray-800'
                          : 'text-gray-900 hover:bg-gray-100'
                      }`}
                    >
                      {date}
                    </div>
                  )
                })}
              </div>
            </div>

            {/* Legend */}
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-green-500 rounded"></div>
                <span className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  Đã điểm danh
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className={`w-4 h-4 rounded ${theme === 'dark' ? 'bg-gold-500/30 border-2 border-gold-500' : 'bg-gold-400/30 border-2 border-gold-400'}`}></div>
                <span className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  Hôm nay
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  )
}

